import React, { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCamera, FiX, FiUpload, FiPackage } = FiIcons;

export default function CollectionForm({ bookingId, driverId, onSubmit, onCancel }) {
  const [bagCount, setBagCount] = useState(1);
  const [itemCount, setItemCount] = useState('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (photos.length + files.length > 5) {
      setError('Maximum 5 photos allowed.');
      return;
    }
    setUploading(true);
    setError('');
    const uploaded = [];
    for (const file of files) {
      try {
        const compressed = await compressImage(file);
        const ext = file.name.split('.').pop();
        const path = `pickups/${bookingId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from('pickup-photos')
          .upload(path, compressed, { contentType: file.type, upsert: false });
        if (uploadErr) throw uploadErr;
        const { data } = supabase.storage.from('pickup-photos').getPublicUrl(path);
        uploaded.push({ url: data.publicUrl, path, preview: URL.createObjectURL(compressed) });
      } catch (err) {
        setError(`Upload failed: ${err.message}`);
      }
    }
    setPhotos(prev => [...prev, ...uploaded]);
    setUploading(false);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (photos.length === 0) {
      setError('Please take at least 1 photo.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { error: insertErr } = await supabase
        .from('pickup_collections')
        .insert({
          booking_id: bookingId,
          driver_id: driverId,
          bag_count: bagCount,
          item_count: itemCount ? parseInt(itemCount) : null,
          notes: notes.trim(),
          photo_urls: photos.map(p => p.url),
          otp_verified: false,
        });
      if (insertErr) throw insertErr;

      const { error: statusErr } = await supabase
        .from('bookings')
        .update({ status: 'pickup_completed' })
        .eq('id', bookingId);
      if (statusErr) throw statusErr;

      onSubmit();
    } catch (e) {
      setError(e.message || 'Submission failed. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="bg-teal-600 px-4 py-3 flex items-center gap-2">
        <FiPackage className="text-white text-lg" />
        <h3 className="font-bold text-white">Collection Details</h3>
      </div>

      <div className="p-4 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-xl text-sm flex items-center gap-2">
            <SafeIcon name="AlertCircle" className="shrink-0" /> {error}
          </div>
        )}

        {/* Bag Count */}
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-2 block">Number of Bags *</label>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setBagCount(prev => Math.max(1, prev - 1))}
              className="h-12 w-12 rounded-xl bg-gray-100 text-2xl font-bold text-brand-navy active:bg-gray-200 transition-colors"
            >−</button>
            <span className="text-3xl font-bold text-brand-navy w-10 text-center">{bagCount}</span>
            <button
              onClick={() => setBagCount(prev => prev + 1)}
              className="h-12 w-12 rounded-xl bg-brand-navy text-white text-2xl font-bold active:opacity-80 transition-opacity"
            >+</button>
          </div>
        </div>

        {/* Item Count */}
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1.5 block">
            Rough Item Count <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <input
            type="number"
            value={itemCount}
            onChange={e => setItemCount(e.target.value)}
            placeholder="e.g. 12"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-400 text-base"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-1.5 block">
            Notes <span className="text-gray-400 font-normal">(Damage / Stains / Special)</span>
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            placeholder="e.g. One shirt has a stain on collar..."
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-400 text-sm resize-none"
          />
        </div>

        {/* Photos */}
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-2 block">
            Photos * <span className="text-gray-400 font-normal">({photos.length}/5)</span>
          </label>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square">
                <img src={p.preview || p.url} alt="" className="w-full h-full object-cover rounded-xl" />
                <button
                  onClick={() => removePhoto(i)}
                  className="absolute -top-1 -right-1 h-6 w-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm"
                >
                  <FiX className="text-xs" />
                </button>
              </div>
            ))}
            {photos.length < 5 && (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 bg-gray-50 active:bg-gray-100 disabled:opacity-50"
              >
                {uploading ? (
                  <svg className="animate-spin h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <>
                    <FiCamera className="text-gray-400 text-2xl" />
                    <span className="text-xs text-gray-400">Add Photo</span>
                  </>
                )}
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={handlePhotoSelect}
          />
          {photos.length === 0 && (
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full py-3 rounded-xl border-2 border-dashed border-teal-300 bg-teal-50 text-teal-700 font-semibold text-sm flex items-center justify-center gap-2 active:bg-teal-100 disabled:opacity-50"
            >
              <FiUpload /> Take / Upload Photos
            </button>
          )}
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || uploading || photos.length === 0}
          className="w-full py-4 bg-teal-600 text-white font-bold rounded-xl text-base flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
        >
          {submitting ? (
            <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <SafeIcon name="CheckCircle" />
          )}
          {submitting ? 'Submitting...' : 'Submit Pickup'}
        </button>

        <button
          onClick={onCancel}
          disabled={submitting}
          className="w-full py-3 text-gray-500 text-sm font-medium active:opacity-70"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Compress image before upload
async function compressImage(file, maxWidth = 1200, quality = 0.8) {
  return new Promise(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => resolve(blob || file), file.type, quality);
    };
    img.onerror = () => resolve(file);
    img.src = url;
  });
}